// system/activation--desktop — license this node to the business before it
// can run on its local database. A PUBLIC, pre-login page (no shell): shown
// whenever erp-core's GET /v1/erp/license/status says `licensed:false` on a
// node — NOT tied to `erp.local-mode` (an unlicensed node's flags are {}, so a
// flag gate would refuse the page exactly when the node needs it).
//
// Variants:
//   public — /activate (NodeLicenceGate redirects here); a licensed node or a
//            cloud deployment (status 404) is sent on to sign in / home.
//   renew  — /system/activation inside the shell, owner only on a node:
//            replace or renew a valid licence (server: owner gate).
//
// The node decides which paths exist (status `activation_methods`, rule 6):
//   code — the «رمز التفعيل» field takes the short code (and a pasted file
//          when `file` is offered too) → POST {activation_code} | {file};
//   qr   — the QR row: GET /license/qr rendered here from `qr_payload`,
//          re-read at `refresh_at`; while it shows, the status is polled
//          because the owner's phone licenses the node over the LAN
//          (POST /license/claim) without anything typed here;
//   file — without `code`, the field is «ملف التفعيل» and takes only the
//          pasted signed file → POST {file};
//   []   — no verification key on the node (reason no_key): nothing to
//          submit, the server's sentence says why.
//
// States: loading · idle · activating · invalid · used · expired · offline ·
// unavailable (code redemption unreachable) · no_key · status error ·
// success (→ /setup while the node has no owner, else sign in, or home
// when already signed in).

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { PrimaryAction } from '@/components/Button';
import { ErrorCard } from '@/components/ErrorCard';
import { Field, TextInput } from '@/components/Form';
import { Skeleton } from '@/components/Skeleton';
import { HOME_PATH } from '@/app/Forbidden';
import { sessionStore } from '@/lib/auth';
import { QrMatrixSvg } from '../components/QrMatrixSvg';
import { formatActivationInput, isActivationFile, isCompleteInput } from '../lib/code';
import {
  ACTIVATION_COPY,
  activationError,
  activationMethods,
  CLAIM_POLL_MS,
  isNotANode,
  useActivateLicence,
  useLicenceQr,
  useLicenceStatus,
  useNodeAuthStatus,
  type ActivationError,
  type LicenceStatus,
} from '../data/api';
import '../system.css';

/** Board copy (exact). «[اسم المنصة]» is a design marker → the product name. */
export const ACTIVATION_TEXT = {
  title: 'احجزلي · سطح المكتب',
  sub: 'هذا الجهاز غير مرخَّص بعد. أدخل رمز التفعيل من «الإعدادات ← الأجهزة» على حساب المالك، أو امسح الرمز من هاتفك.',
  field: 'رمز التفعيل',
  /** Not drawn: the label when the node offers the file path without the code (OPEN-QUESTIONS). */
  fileField: 'ملف التفعيل',
  placeholder: 'XK7P-••••-••••',
  qrHint: 'أو من تطبيق احجزلي للأعمال: الإعدادات ← الأجهزة ← مسح',
  qrLabel: 'رمز QR لهذا الجهاز',
  cta: 'تفعيل هذا الجهاز',
  footer2: 'الترخيص بالجهاز لا بالشخص؛ النزع من الإعدادات.',
} as const;

/** erp-core sends the Go GOOS key; the footer names the system in Arabic. */
export const OS_LABELS: Record<string, string> = {
  windows: 'ويندوز',
  linux: 'لينكس',
  darwin: 'ماك',
};

export interface ActivationScreenProps {
  variant?: 'public' | 'renew';
}

export function ActivationScreen({ variant = 'public' }: ActivationScreenProps) {
  const navigate = useNavigate();
  const [qrShown, setQrShown] = useState(false);
  const status = useLicenceStatus(true, qrShown ? CLAIM_POLL_MS : false);
  const activate = useActivateLicence();
  // Public page only: does the node still need its owner (first run)?
  const authStatus = useNodeAuthStatus(variant === 'public' && !sessionStore.isAuthenticated());
  const needsOwner = authStatus.data?.bootstrap_required === true;
  const [value, setValue] = useState('');
  const [error, setError] = useState<ActivationError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const st = status.data;
  const methods = activationMethods(st);
  const codeOffered = methods.includes('code');
  const fileOffered = methods.includes('file');
  const qrOffered = methods.includes('qr');
  const showField = !!st && (codeOffered || fileOffered);
  const qr = useLicenceQr(!!st && qrOffered);
  const qrData = qr.data && !qr.isError ? qr.data : undefined;

  // Seen unlicensed → a later licensed status came from the phone's claim.
  const sawUnlicensed = useRef(false);
  if (st && !st.licensed) sawUnlicensed.current = true;
  // renew: the licence that was in place when the page opened.
  const firstIssued = useRef<string | null | undefined>(undefined);
  if (st && firstIssued.current === undefined) firstIssued.current = st.issued_at;

  useEffect(() => {
    setQrShown(!!qrData);
  }, [qrData]);

  useEffect(() => {
    if (showField) inputRef.current?.focus();
  }, [showField]);

  // renew: the owner's phone replaced the licence while the QR was shown.
  const claimedOnRenew =
    variant === 'renew' && !!st?.licensed && firstIssued.current !== undefined && st.issued_at !== firstIssued.current && !activate.isPending && !activate.isSuccess;
  useEffect(() => {
    if (claimedOnRenew) navigate(HOME_PATH, { replace: true });
  }, [claimedOnRenew, navigate]);

  // The public page on a deployment it does not apply to: carry on.
  if (variant === 'public') {
    if (status.isError && isNotANode(status.error)) return <Navigate to="/auth/login" replace />;
    if (st?.licensed && !activate.isPending && !activate.isSuccess) {
      if (sessionStore.isAuthenticated()) return <Navigate to={HOME_PATH} replace />;
      if (needsOwner) return <Navigate to="/setup" replace state={sawUnlicensed.current ? { notice: ACTIVATION_COPY.activatedFirstRun } : undefined} />;
      return <Navigate to="/auth/login" replace state={sawUnlicensed.current ? { notice: ACTIVATION_COPY.activated } : undefined} />;
    }
  }

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (activate.isPending || !showField) return;
    if (!value.trim()) {
      inputRef.current?.focus();
      return;
    }
    // Only what the node offers is ever posted: without `code` a typed code
    // never leaves the device.
    const asFile = isActivationFile(value);
    if ((asFile && !fileOffered) || (!asFile && !codeOffered) || !isCompleteInput(value)) {
      setError({ kind: 'invalid', message: codeOffered ? ACTIVATION_COPY.invalid : ACTIVATION_COPY.fileInvalid });
      inputRef.current?.focus();
      return;
    }
    setError(null);
    try {
      const res = await activate.mutateAsync(value);
      if (!res.licensed) {
        const expired = res.reason === 'expired';
        setError({ kind: expired ? 'expired' : 'invalid', message: expired ? ACTIVATION_COPY.expired : ACTIVATION_COPY.invalid });
        return;
      }
      if (sessionStore.isAuthenticated()) navigate(HOME_PATH, { replace: true });
      // A node with no owner yet goes on to its first run (/setup), else to sign in.
      else if (needsOwner) navigate('/setup', { replace: true, state: { notice: ACTIVATION_COPY.activatedFirstRun } });
      else navigate('/auth/login', { replace: true, state: { notice: ACTIVATION_COPY.activated } });
    } catch (err) {
      setError(activationError(err));
      inputRef.current?.focus();
    }
  };

  const noKey = !!st && methods.length === 0;
  const statusFailed = status.isError && !isNotANode(status.error);
  const state = status.isLoading
    ? 'loading'
    : statusFailed
      ? 'status-error'
      : noKey
        ? 'no_key'
        : activate.isPending
          ? 'activating'
          : error
            ? error.kind
            : 'idle';
  const fieldError = error && (error.kind === 'invalid' || error.kind === 'used' || error.kind === 'expired') ? error.message : null;
  const pageError = error && !fieldError ? error.message : null;

  return (
    <div
      className={`e-center sys-act-page${variant === 'renew' ? ' sys-act-page--inset' : ''}`}
      data-testid="activation-screen"
      data-variant={variant}
      data-state={state}
      data-methods={methods.join(',')}
    >
      <form className="sys-act-card" onSubmit={submit} noValidate aria-labelledby={titleId}>
        <img className="sys-act-mark" src="/brand/logo-ehjizli.svg" alt="" aria-hidden="true" />
        <div>
          <h1 id={titleId} className="sys-act-title">
            {ACTIVATION_TEXT.title}
          </h1>
          <p className="sys-act-sub">{ACTIVATION_TEXT.sub}</p>
        </div>
        {st && !st.licensed && st.reason === 'expired' && (
          <p className="sys-act-warn" role="status" data-testid="activation-licence-expired">
            {ACTIVATION_COPY.licenceExpired}
          </p>
        )}
        {status.isLoading && (
          <div data-testid="activation-loading" aria-busy="true">
            <Skeleton height={52} radius={16} />
            <span className="sr-only">جارٍ التحميل</span>
          </div>
        )}
        {statusFailed && <ErrorCard error={status.error} kind="network" localSave={false} onRetry={() => void status.refetch()} />}
        {noKey && (
          <p className="sys-act-alert" role="status" data-testid="activation-no-key">
            {ACTIVATION_COPY.keyMissing}
          </p>
        )}
        {showField && (
          <Field label={codeOffered ? ACTIVATION_TEXT.field : ACTIVATION_TEXT.fileField} error={fieldError}>
            {(id) => (
              <TextInput
                id={id}
                ref={inputRef}
                ltr
                className="sys-act-code"
                placeholder={codeOffered ? ACTIVATION_TEXT.placeholder : undefined}
                autoComplete="off"
                autoCapitalize={codeOffered ? 'characters' : 'off'}
                spellCheck={false}
                value={value}
                invalid={!!fieldError}
                readOnly={activate.isPending}
                data-testid="activation-code"
                data-accepts={codeOffered ? (fileOffered ? 'code,file' : 'code') : 'file'}
                onChange={(e) => {
                  // Without `code` the field holds a pasted file verbatim (never hyphenated).
                  setValue(codeOffered ? formatActivationInput(e.target.value) : e.target.value);
                  if (error) setError(null);
                }}
              />
            )}
          </Field>
        )}
        {qrData && (
          <div className="sys-act-qr" data-testid="activation-qr" data-nonce={qrData.nonce}>
            <QrMatrixSvg className="sys-act-qr-img" value={qrData.qr_payload} label={ACTIVATION_TEXT.qrLabel} data-testid="activation-qr-code" />
            <p>{ACTIVATION_TEXT.qrHint}</p>
          </div>
        )}
        {showField && (
          <PrimaryAction type="submit" size="lg" block loading={activate.isPending} data-testid="activation-submit">
            {ACTIVATION_TEXT.cta}
          </PrimaryAction>
        )}
        {pageError && (
          <p className="sys-act-alert" role="alert" data-testid="activation-alert">
            {pageError}
          </p>
        )}
        <Footer status={st} />
      </form>
    </div>
  );
}

/** «الجهاز: … · … · v… · مرخَّص للنشاط «…»» — each part only when the server sent it (rule 7). */
function Footer({ status }: { status: LicenceStatus | undefined }) {
  const device = status?.device_name || null;
  const nodeId = status?.node_id || null;
  const os = status?.os ? (OS_LABELS[status.os] ?? status.os) : null;
  const hasLine1 = !!(device || nodeId);
  return (
    <p className="sys-act-foot" data-testid="activation-footer">
      {hasLine1 && (
        <span data-testid="activation-device">
          {'الجهاز: '}
          {device ? (
            <bdi>{device}</bdi>
          ) : (
            <bdi dir="ltr" className="ltr">
              {nodeId}
            </bdi>
          )}
          {os ? (
            <>
              {' · '}
              {OS_LABELS[status!.os] ? (
                os
              ) : (
                <bdi dir="ltr" className="ltr">
                  {os}
                </bdi>
              )}
            </>
          ) : null}
          {status?.app_version ? (
            <>
              {' · '}
              <bdi dir="ltr" className="ltr" data-testid="activation-version">
                {status.app_version}
              </bdi>
            </>
          ) : null}
          {status?.licensee ? ` · مرخَّص للنشاط «${status.licensee}»` : null}
        </span>
      )}
      {hasLine1 && <br />}
      {ACTIVATION_TEXT.footer2}
    </p>
  );
}

export default ActivationScreen;
